// src/pages/Users/News/utils/newsUtils.js

import { toAbsolutePhotoUrl } from "../../../../services/newsPhotoServices";

export const NEWS_STORAGE_KEY = "news_ui_state";

export const DEFAULT_NEWS_UI = {
  search: "",
  showInactive: false,
  sort: "dateDesc",
  perPage: 9,
};

export const FALLBACK_NEWS_IMG = "/img/no-image.jpg";

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function readNewsStorage() {
  try {
    const saved = localStorage.getItem(NEWS_STORAGE_KEY);

    if (!saved) return DEFAULT_NEWS_UI;

    return {
      ...DEFAULT_NEWS_UI,
      ...JSON.parse(saved),
    };
  } catch {
    return DEFAULT_NEWS_UI;
  }
}

export function strip(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(html = "") {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = String(html);

  return (div.textContent || div.innerText || "").trim();
}

export function sanitizeLight(html = "") {
  try {
    const doc = new DOMParser().parseFromString(String(html || ""), "text/html");

    doc.querySelectorAll("script").forEach((node) => node.remove());

    doc.querySelectorAll("a").forEach((anchor) => {
      const href = anchor.getAttribute("href") || "";

      if (/^\s*javascript:/i.test(href)) {
        anchor.removeAttribute("href");
      }

      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    });

    return doc.body.innerHTML || "";
  } catch {
    return "";
  }
}

export function truncateText(value = "", maxLength = 220) {
  const clean = stripHtml(value);

  if (!clean) return "";

  if (clean.length <= maxLength) return clean;

  return `${clean.slice(0, maxLength).trim()}…`;
}

export function getAuthorName(news) {
  const firstName = news?.author?.prenom || "";
  const lastName = news?.author?.nom || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || "Club";
}

export function newsDateValue(news) {
  return (
    new Date(
      news?.created_at ||
        news?.createdAt ||
        news?.updated_at ||
        news?.updatedAt ||
        0
    ).getTime() ||
    Number(news?.id) ||
    0
  );
}

export function formatNewsDate(value) {
  const date = new Date(value || Date.now());

  if (Number.isNaN(date.valueOf())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function getNewsDate(news) {
  return (
    news?.created_at ||
    news?.createdAt ||
    news?.updated_at ||
    news?.updatedAt ||
    null
  );
}

export function getNewsCover(news) {
  return toAbsolutePhotoUrl(news?.image_url || "") || FALLBACK_NEWS_IMG;
}

export function canViewNews(news, isManager = false, showInactive = false) {
  if (!news) return false;
  if (news.is_active) return true;
  return isManager && showInactive;
}

export function filterAndSortNews(newsList = [], ui, isManager = false) {
  const safeUi = {
    ...DEFAULT_NEWS_UI,
    ...(ui || {}),
  };

  const query = strip(safeUi.search);

  let base = Array.isArray(newsList) ? newsList.filter(Boolean) : [];

  base = base.filter((news) => canViewNews(news, isManager, safeUi.showInactive));

  if (query) {
    base = base.filter((news) => {
      const haystack = [
        news?.title,
        stripHtml(news?.content),
        getAuthorName(news),
      ]
        .map((value) => strip(value))
        .join(" ");

      return haystack.includes(query);
    });
  }

  base.sort((a, b) => {
    if (safeUi.sort === "alpha") {
      return collatorFR.compare(String(a?.title || ""), String(b?.title || ""));
    }

    const dateA = newsDateValue(a);
    const dateB = newsDateValue(b);

    if (safeUi.sort === "dateAsc") {
      return (
        dateA - dateB ||
        collatorFR.compare(String(a?.title || ""), String(b?.title || ""))
      );
    }

    return (
      dateB - dateA ||
      collatorFR.compare(String(a?.title || ""), String(b?.title || ""))
    );
  });

  return base;
}

export function getFeaturedNews(
  newsList = [],
  isManager = false,
  showInactive = false,
  limit = 6
) {
  return (Array.isArray(newsList) ? newsList.filter(Boolean) : [])
    .filter((news) => canViewNews(news, isManager, showInactive))
    .sort((a, b) => newsDateValue(b) - newsDateValue(a))
    .slice(0, limit);
}

export function hasActiveNewsFilters(ui, isManager = false) {
  const safeUi = {
    ...DEFAULT_NEWS_UI,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.search) ||
    safeUi.sort !== DEFAULT_NEWS_UI.sort ||
    (isManager && safeUi.showInactive)
  );
}